// @givmo/board — the words of a director's Board in the officer Board's look (CBG-UX10; design
// stop design-283's redraw C, frames c1–c7, `scratchpad/2026-10-24-director-home-design/c/`).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved C for building with four answers (D-CBG-40, 2026-10-24 14:40 PDT):
// the director's pages take the officer pages' names. Its new lines were drafts until CBG-UX10's copy
// round, CBG-UX-COPY3, approved them (D-CBG-45, 2026-10-29 08:53 PDT, rows D1–D5), two reworded;
// CBG-FIX145's two were drafts until CBG-UX-COPY5 approved them (D-CBG-52, 2026-11-01 11:53 PST),
// "minute book" in small letters as Board's other sentences write it. A line that reuses an
// approved one says so and imports it where it lives, so the two never drift: the officer pages'
// titles and lines, the meeting page's acts, the retired card's heading. Each line names the frame
// that draws it, or "not drawn" for a state no frame shows.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the board recorded and where it shows, never a duty,
// a deadline or a judgment.

import { coiWords } from '../conflicts/coi-words';
import { committeesWords } from '../committees/committees-words';
import { onboardingWords } from '../onboarding/onboarding-words';
import { minutesConsentWords } from '../consents/minutes-consent-words';
import { minuteBookWords } from '../records/minute-book-words';

export const directorPagesWords = {
  // ── The frame (every frame) ─────────────────────────────────────────────────────────────────────

  // Approved: the officer frame's bar word, the app the page is in (Org Machinery OM-26).
  appWord: 'Board',

  // ── Meetings (frames c1, c2, c7) ────────────────────────────────────────────────────────────────

  // Approved: the officer's Meetings line.
  meetingsLine: 'Your board’s upcoming and past meetings.',
  // Approved: About the board's heading for the board's recorded meeting rules, here the way to them.
  howMeetingsWork: onboardingWords.howMeetingsWork,
  // Approved: the officer's group labels.
  upcoming: 'Upcoming',
  past: 'Past',
  // Approved (CBG-UX-COPY3's D1), drawn c1: a board that has scheduled nothing yet.
  noMeetingsTitle: 'No meetings scheduled yet',
  noMeetingsLine: 'When your board schedules a meeting, it will appear here.',
  // Approved (CBG-UX-COPY3's D1), not drawn (design.md frame h3): meetings behind, none ahead.
  nothingAheadTitle: 'Nothing scheduled right now',
  nothingAheadLine: 'Your board’s next meeting will appear here once it’s scheduled.',
  // Approved: the meeting page's RSVP heading and its packet card's act.
  canYouMakeIt: 'Can you make it?',
  // Approved (CBG-UX-COPY2's A5f): beside a director's answer, the way to change it, a link to the
  // meeting's page where the answer is given, as "Can you make it?" is.
  changeAnswer: 'Change',
  openThePacket: 'Open the packet',
  // Approved (CBG-UX-COPY5, D-CBG-52), not drawn: a past meeting whose own minutes are the record —
  // the way to them from the meeting's page, where the Minute book was the only one. Said of what
  // Board recorded, never who approved them or how (CBG-FIX70's line is the approval line's to say).
  // "minute book" in small letters, as every other sentence writes it; the capital is the menu's.
  meetingMinutesTitle: 'The minutes of this meeting',
  meetingMinutesLine: 'Approved, and in the minute book.',
  // Approved (CBG-RC9-COPY1): the consent page's way to the minutes it names, the same act here.
  readTheMinutes: minutesConsentWords.readMinutes,

  // ── Written consents (frame c3) ─────────────────────────────────────────────────────────────────

  // Approved: the officer's title (it was "Things to sign").
  writtenConsents: 'Written consents',
  // Approved: the officer's Written consents line.
  consentsLine:
    'How your board, or one of its committees, acts without holding a meeting. Every seat it goes out to has to sign before it becomes an act of the board, or of the committee whose consent it is.',
  // Approved: the officer's chips, and the retired card's heading (CBG-FIX109).
  showAll: 'All',
  showWaiting: coiWords.waitingOnYou,
  showSignedByEveryone: 'Signed by everyone',
  // Approved: the shipped director page's mark.
  needsYourSignature: 'Needs your signature',
  // Approved (CBG-UX-COPY3's D2), drawn c3.
  readAndSign: 'Read and sign',
  youSigned: (day: string) => `You signed ${day}`,
  // Approved (CBG-UX-COPY3's D2), not drawn: a board that has sent nothing to sign; the line names
  // the act as the page's own line above it does ("acts without holding a meeting").
  noConsentsTitle: 'No written consents yet',
  noConsentsLine:
    'When your board acts without a meeting, what it asks you to sign will appear here.',
  // Approved: the officer's consent page's way back.
  allWrittenConsents: 'All written consents',

  // ── Minute book (frame c4) ──────────────────────────────────────────────────────────────────────

  // Approved: the officer's title, and its record page's way back.
  minuteBook: 'Minute book',
  // Approved (CBG-UX-COPY3's D3), drawn c4: the officer's line, said to a director.
  bookLine:
    'Your board’s records: approved minutes, written consents and resolutions, including records from before your board used Givmo.',
  // Approved (CBG-UX-COPY3's D3), drawn c4 (CBG-FIX140): a draft, never the record; after its
  // standing word ("Draft · not the record until your board approves it"), in the book and on its page.
  readTheDraft: 'Read the draft',
  untilApproved: 'not the record until your board approves it',
  // Approved (CBG-UX-COPY3's D4; review-291's M1), not drawn: a record that isn't the record yet
  // says when its draft was started (its `created_at`) where the record says "Entered".
  draftStarted: (when: string) => `Draft started ${when}`,
  // Approved: the officer's empty book, a search that finds nothing, and a book whose every record
  // is still in progress (`minute-book-words.ts`).
  bookEmptyTitle: 'Nothing in the minute book yet',
  noMatch: 'Nothing matches.',
  nothingApprovedYet: minuteBookWords.nothingApprovedYet,
  // Approved (CBG-UX-COPY3's D3), not drawn: when an empty book's records will appear, in the shape
  // the other empty pages use, since the book's own line above it already says what it holds.
  bookEmptyLine: 'When your board adds a record, it will appear here.',
  // Approved: the officer's group label for minutes still being drafted.
  inProgress: minuteBookWords.inProgress,

  // ── Board members (frame c5) ────────────────────────────────────────────────────────────────────

  // Approved: the officer's title and first column.
  membersTitle: 'Board members',
  columnMember: 'Member',
  // Approved (CBG-UX-COPY3's D5), drawn c5.
  membersLine: 'Everyone who serves on this board, and what each does.',
  you: 'You',
  // Approved: About the board's card, under the table.
  whoDoesWhat: onboardingWords.whoDoesWhat,

  // ── Committees (not drawn: the page C names for a seat on a committee) ─────────────────────────

  // Approved: the officer's title, and its committees' roles.
  committeesTitle: 'Committees',
  columnCommittee: committeesWords.columnCommittee,
  roleChair: committeesWords.roleChair,
  roleMember: committeesWords.roleMember,
  // Approved (CBG-UX-COPY3's D5), not drawn.
  committeesLine: 'The committees you serve on, and when each meets next.',
  columnYourRole: 'Your role',
  noCommitteesTitle: 'You’re not on any of this board’s committees',
  noCommitteesLine: 'When your board adds you to a committee, it will appear here.',

  // ── Conflicts of interest (frame c6; D-CBG-40's third answer) ──────────────────────────────────

  // Approved: the officer's page name, everywhere "What you’ve told the board" was.
  conflictsTitle: 'Conflicts of interest',
  // Approved: the officer register's citation, "Open the record" since CBG-UX-COPY2's A5c.
  thisRecord: 'Open the record',
} as const;
